// src/components/ResetButton.tsx

import { ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Button, Modal, Tooltip } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import type React from "react";
import { useCallback } from "react";
import useAppStore from "@/store/useAppStore";

interface ResetButtonProps {
  className?: string;
}

export const ResetButton: React.FC<ResetButtonProps> = ({ className }) => {
  const forceReset = useAppStore((state) => state.forceReset);

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      Modal.confirm({
        title: (
          <div className="flex items-center gap-3 text-gray-800">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-lg font-semibold">Confirm Reset</span>
          </div>
        ),
        content: (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-4 space-y-4"
          >
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-700 space-y-2">
                <p>This action will:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Reset all processing states</li>
                  <li>Clear any uploaded documents</li>
                  <li>Reload the application</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-2 text-amber-800 bg-amber-50 p-3 rounded-md">
              <WarningOutlined className="mt-0.5 flex-shrink-0" />
              <p className="text-sm">Any unsaved changes or in-progress operations will be lost.</p>
            </div>
          </motion.div>
        ),
        okText: "Yes, reset application",
        cancelText: "Cancel",
        onOk: forceReset,
        okButtonProps: {
          className: "bg-red-600 hover:bg-red-700 border-none text-white h-9 px-4 rounded-lg",
          icon: <ReloadOutlined />,
        },
        cancelButtonProps: {
          className: "border-gray-300 text-gray-700 hover:bg-gray-50 h-9 px-4 rounded-lg",
        },
        width: 480,
        className: "reset-confirmation-modal",
        maskClosable: true,
        centered: true,
      });
    },
    [forceReset],
  );

  return (
    <Tooltip
      title={
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="p-1"
        >
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-200 flex-shrink-0 mt-0.5" />
            <div className="text-sm font-medium text-white">Emergency Reset Function</div>
          </div>
          <div className="text-xs text-gray-200 pl-6">
            Use this to reset the application state if you encounter errors or the app becomes
            unresponsive.
            <div className="mt-2 px-2 py-1 bg-gray-700/50 rounded flex items-center justify-between">
              <span>Keyboard shortcut:</span>
              <kbd className="px-2 py-0.5 bg-gray-800 text-gray-200 rounded font-mono text-xs">
                Ctrl + Alt + R
              </kbd>
            </div>
          </div>
        </motion.div>
      }
      overlayClassName="reset-button-tooltip"
      overlayInnerStyle={{
        backgroundColor: "#1f2937",
        borderRadius: "0.75rem",
        padding: "0.75rem",
        maxWidth: "300px",
      }}
      placement="top"
    >
      <Button
        onClick={handleReset}
        type="primary"
        danger
        size="large"
        icon={<ReloadOutlined />}
        className={`
          flex items-center justify-center
          bg-red-600 hover:bg-red-700
          border-none
          text-white
          rounded-lg
          px-4 py-2
          w-full sm:w-auto
          mt-0 sm:mt-0
          shadow-sm hover:shadow-md
          transition-all duration-200
          ${className}
        `}
      >
        Reset Processing State
      </Button>
    </Tooltip>
  );
};

// Add this to your global CSS file
const globalStyles = `
.reset-button-tooltip .ant-tooltip-arrow-content {
  background-color: #1f2937 !important;
}

.reset-confirmation-modal .ant-modal-content {
  padding: 24px;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
}

.reset-confirmation-modal .ant-modal-footer {
  margin-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.reset-confirmation-modal .ant-modal-footer .ant-btn {
  font-weight: 500;
}
`;

export default ResetButton;
